import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, BookOpen, Check, Menu, X, HelpCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { topicsApi, Topic } from '../api/topics';
import { useProgressStore } from '../state/progressStore';
import { useModuleCompletion } from '../hooks/useModuleCompletion';

export const SectionDetail: React.FC = () => {
  const { slug, sectionIndex } = useParams<{ slug: string; sectionIndex: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { updateProgress } = useProgressStore();
  const { markModuleComplete, isModuleComplete } = useModuleCompletion();

  const currentIndex = parseInt(sectionIndex || '0', 10);
  const isCurrentModuleComplete = topic ? isModuleComplete(topic.id, currentIndex) : false;

  useEffect(() => {
    const fetchTopic = async () => {
      if (!slug) return;
      
      try {
        const data = await topicsApi.getBySlug(slug);
        setTopic(data);
        
        // Update progress
        if (data.sections && data.sections.length > 0) {
          const progress = ((currentIndex + 1) / data.sections.length) * 100;
          updateProgress(data.id, { read_pct: Math.round(progress) });
        }
      } catch (error) {
        console.error('Error fetching topic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug, currentIndex, updateProgress]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!topic || !topic.sections || topic.sections.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Content niet gevonden</h1>
        <Link to="/topics" className="text-blue-600 hover:underline">
          Terug naar delen
        </Link>
      </div>
    );
  }

  const currentSection = topic.sections[currentIndex];
  const totalSections = topic.sections.length;
  const progressPercentage = ((currentIndex + 1) / totalSections) * 100;
  
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalSections - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      navigate(`/topics/${slug}/section/${currentIndex - 1}`);
    }
  };

  const handleNext = () => {
    // Mark current module as complete before moving to next
    if (topic && !isCurrentModuleComplete) {
      markModuleComplete(topic.id, currentIndex);
    }
    
    if (hasNext) {
      navigate(`/topics/${slug}/section/${currentIndex + 1}`);
    }
  };

  const handleMarkComplete = () => {
    if (topic) {
      markModuleComplete(topic.id, currentIndex);
    }
  };

  // Generate a unique image for each section based on content - no duplicates
  const getSectionImage = (index: number, heading: string): string => {
    const headingLower = heading.toLowerCase();
    
    // AI Fundamentals - Learning/education image
    if (headingLower.includes('fundamentals') || headingLower.includes('intro') || index === 0) {
      return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=300&fit=crop&crop=center';
    } 
    // Data & AI - Data visualization image
    else if (headingLower.includes('data') || headingLower.includes('analyse')) {
      return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=300&fit=crop&crop=center';
    } 
    // GPT models - Neural network/AI image
    else if (headingLower.includes('gpt') || headingLower.includes('revolutie') || headingLower.includes('model')) {
      return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=300&fit=crop&crop=center';
    } 
    // Competition - Business/competition image
    else if (headingLower.includes('concurrentie') || headingLower.includes('competition')) {
      return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=300&fit=crop&crop=center';
    } 
    // Prompt Engineering - Coding/programming image
    else if (headingLower.includes('prompt') || headingLower.includes('engineering') || headingLower.includes('best practices')) {
      return 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=300&fit=crop&crop=center';
    } 
    // Applications & Future - Innovation/future image
    else if (headingLower.includes('toepassingen') || headingLower.includes('toekomst') || headingLower.includes('applications') || headingLower.includes('future')) {
      return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=300&fit=crop&crop=center';
    }
    
    // Unique fallback images based on index to ensure no duplicates
    const uniqueImages = [
      'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=300&fit=crop&crop=center', // Learning
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=300&fit=crop&crop=center', // Data
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=300&fit=crop&crop=center', // AI/Neural
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=300&fit=crop&crop=center', // Business
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=300&fit=crop&crop=center', // Coding
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=300&fit=crop&crop=center', // Innovation
      'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&h=300&fit=crop&crop=center', // Vision
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=300&fit=crop&crop=center', // Technology
    ];
    
    return uniqueImages[index % uniqueImages.length];
  };
  
  const sectionImage = getSectionImage(currentIndex, currentSection.heading);

  return (
    <>
      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Sidebar Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Modules</h3>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Module List */}
        {topic && (
          <div className="p-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Deel:</p>
              <p className="font-medium text-gray-900">{topic.title}</p>
            </div>
            
            <div className="space-y-2">
              {topic.sections.map((section, index) => {
                const isCompleted = isModuleComplete(topic.id, index);
                const isCurrent = index === currentIndex;
                
                return (
                  <Link
                    key={section.id}
                    to={`/topics/${slug}/section/${index}`}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`block px-4 py-3 rounded-lg transition-all ${
                      isCurrent
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : isCompleted
                        ? 'bg-green-50 border border-green-200 hover:bg-green-100'
                        : 'border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1 min-w-0">
                        <span className={`text-xs font-medium mr-3 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                          isCurrent
                            ? 'bg-blue-500 text-white'
                            : isCompleted
                            ? 'bg-green-200 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isCompleted && !isCurrent ? <Check className="w-3 h-3" /> : index + 1}
                        </span>
                        <span className={`text-sm truncate ${
                          isCurrent
                            ? 'font-semibold text-blue-900'
                            : isCompleted
                            ? 'text-gray-700'
                            : 'text-gray-900'
                        }`}>
                          {section.heading}
                        </span>
                      </div>
                      {isCompleted && (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header with Menu Button and Back Link */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Menu className="w-5 h-5 mr-2" />
              Modules
            </button>

            <Link 
              to={`/topics/${slug}`}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terug naar overzicht
            </Link>
          </div>
        </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>{topic.title}</span>
          <span className="font-medium">
            {currentIndex + 1} van {totalSections}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Hero image */}
      <div className="relative h-64 mb-6 rounded-lg overflow-hidden">
        <img
          src={sectionImage}
          alt={currentSection.heading}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold drop-shadow-lg">
              {currentSection.heading}
            </h1>
            <div className="mt-2 text-sm text-white/90">
              Module {currentIndex + 1} van {totalSections}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-8 mb-6">
        <div className="prose prose-gray max-w-none">
          <ReactMarkdown
            components={{
              h3: ({ node, children, ...props }) => (
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3" {...props}>
                  {children}
                </h3>
              ),
              h4: ({ node, children, ...props }) => (
                <h4 className="text-base font-semibold text-gray-800 mt-4 mb-2" {...props}>
                  {children}
                </h4>
              ),
              p: ({ node, children, ...props }) => (
                <p className="text-gray-700 leading-7 mb-4" {...props}>
                  {children}
                </p>
              ),
              ul: ({ node, children, ...props }) => (
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4" {...props}>
                  {children}
                </ul>
              ),
              ol: ({ node, children, ...props }) => (
                <ol className="list-decimal list-inside text-gray-700 space-y-2 mb-4 ml-4" {...props}>
                  {children}
                </ol>
              ),
              li: ({ node, children, ...props }) => (
                <li className="text-gray-700 leading-7" {...props}>
                  {children}
                </li>
              ),
              strong: ({ node, children, ...props }) => (
                <strong className="font-semibold text-gray-900" {...props}>
                  {children}
                </strong>
              ),
              em: ({ node, children, ...props }) => (
                <em className="italic text-gray-700" {...props}>
                  {children}
                </em>
              ),
              blockquote: ({ node, children, ...props }) => (
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-4" {...props}>
                  {children}
                </blockquote>
              ),
            }}
          >
            {currentSection.body_md}
          </ReactMarkdown>
        </div>
      </div>

      {/* Quiz Section - Always show call-to-action */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <HelpCircle className="w-7 h-7" />
                <h2 className="text-2xl font-bold">Toets je kennis</h2>
              </div>
              <p className="text-lg opacity-90 mb-2">
                {topic.questions && topic.questions.length > 0 
                  ? `Test wat je hebt geleerd met ${topic.questions.length} vragen`
                  : 'Test je kennis met de toetsvragen voor deze module'
                }
              </p>
              <p className="text-sm opacity-80">
                Flashcard format: 1 vraag per keer voor optimale focus
              </p>
            </div>
            {topic.questions && topic.questions.length > 0 ? (
              <button
                onClick={() => navigate(`/topics/${slug}/section/${currentIndex}/quiz`)}
                className="px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg shadow-lg"
              >
                Toets je kennis →
              </button>
            ) : (
              <button
                disabled
                className="px-8 py-4 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-semibold text-lg shadow-lg"
              >
                Binnenkort beschikbaar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Completion status */}
      {isCurrentModuleComplete && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center text-green-700">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="font-medium">Deze module is voltooid!</span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={handlePrevious}
          disabled={!hasPrevious}
          className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-colors ${
            hasPrevious
              ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              : 'bg-gray-50 text-gray-400 cursor-not-allowed'
          }`}
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Vorige
        </button>

        <div className="flex items-center space-x-3">
          {!isCurrentModuleComplete && (
            <button
              onClick={handleMarkComplete}
              className="inline-flex items-center px-4 py-2 rounded-md font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
            >
              <Check className="w-4 h-4 mr-2" />
              Markeer als voltooid
            </button>
          )}
          
          {currentIndex === totalSections - 1 && (
            <div className="flex items-center text-green-600 text-sm font-medium">
              <CheckCircle className="w-5 h-5 mr-1" />
              Laatste module!
            </div>
          )}
        </div>

        {hasNext ? (
          <button
            onClick={handleNext}
            className="inline-flex items-center px-6 py-3 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Volgende
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        ) : (
          <Link
            to={`/topics/${slug}`}
            onClick={() => {
              if (topic && !isCurrentModuleComplete) {
                markModuleComplete(topic.id, currentIndex);
              }
            }}
            className="inline-flex items-center px-6 py-3 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Deel voltooid!
          </Link>
        )}
      </div>
    </div>
    </>
  );
};

